import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export default function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <i className="ri-loader-4-line animate-spin text-4xl text-sky-500"></i>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.error) {
        setError('Giriş yapılamadı. E-posta veya şifre hatalı.');
      }
    } catch {
      setError('Bağlantı sırasında bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminSetup = async () => {
    setSetupLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: 'admin@sadem.com',
        password: 'Sadem2024!',
        options: {
          data: {
            ad: 'Admin',
            soyad: 'Yonetici',
            rol: 'Yonetici',
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!data.user) throw new Error('Kullanici oluşturulamadi');

      setSuccess('Admin kullanicisi oluşturuldu! E-posta: admin@sadem.com, Şifre: Sadem2024!');
      setEmail('admin@sadem.com');
      setPassword('Sadem2024!');
    } catch (err: unknown) {
      let msg = 'Admin oluşturulurken hata oluştu';
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === 'string') {
        msg = err;
      }
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('user already registered')) {
        setError('Bu e-posta adresi zaten kayitli. Doğrudan giriş yapabilirsiniz.');
        setEmail('admin@sadem.com');
        setPassword('Sadem2024!');
      } else {
        setError(msg);
      }
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-drop-fill text-white text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Sadem Su Arıtma</h1>
          <p className="text-sm text-gray-500 mt-1">Servis Yönetim Paneli</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-800 mb-1">Giriş Yap</h2>
          <p className="text-sm text-gray-400 mb-6">Devam etmek için hesabinizla giriş yapin</p>

          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl">
              <i className="ri-checkbox-circle-line flex-shrink-0"></i>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">E-posta</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white"
                  placeholder="ornek@sadem.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Şifre</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="password"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Giriş yapiliyor...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line"></i> Giriş Yap
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-3 text-center">
              İlk kez mi kullaniyorsunuz? Admin hesabi oluşturun:
            </p>
            <button
              onClick={handleAdminSetup}
              disabled={setupLoading}
              className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {setupLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Admin oluşturuluyor...
                </>
              ) : (
                <>
                  <i className="ri-admin-line"></i> Admin Kullanicisi Oluştur
                </>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Yetkisiz erişim yasaktir. Tüm girişler kayit altina alinmaktadir.
        </p>
      </div>
    </div>
  );
}